import React, { Dispatch, SetStateAction, useContext, useMemo, useState } from "react";

import { useSpring, animated } from "@react-spring/web";

import { EditLessonContent } from "./lesson";
import { EditAssessmentContent } from "./assessment";
import { EditPodcastContent } from "./podcast";

import Button from "src/components/button";
import Slider from "src/components/slider";
import { AddLearningBrowser } from "../../components/addLearningBrowser";
import { AddAssetsDialog, AssetSelection } from "src/components/asset/add";
import { EditSimulationContent } from "./simulation";

import PlaceholderEmptyMessage from "src/components/placeholderEmptyMessage";
import SettingsNotFoundSvgIcon from "src/legacy/images/404errorMessage.svg";

import { Course, SimulationContent } from "src/types/coaching/courses";
import { CourseContext } from "../../context";

import _ from "lodash";
import cx from 'classnames';
import styles from "./content.module.scss";

interface EditCourseContentProps {
  course: Course;
  contentIndex: number;

  // eslint-disable-next-line no-unused-vars
  onAddLessonClicked?: (parentUuid?: string | null) => void;

  // eslint-disable-next-line no-unused-vars
  onAddAssessmentClicked?: (parentUuid?: string | null) => void;

  // eslint-disable-next-line no-unused-vars
  onAddSectionClicked: (order: number) => void;
  onContentTypeSelect: (id?: string) => void;
}

export const EditCourseContent: React.FC<EditCourseContentProps> = ({ course, onAddLessonClicked, onAddAssessmentClicked, onAddSectionClicked, onContentTypeSelect }) => {
  const courseContext = React.useContext(CourseContext);
  const courseContent = courseContext.courseContent;

  const entityType = useMemo(() => {
    return _.get(courseContent, "entityType");
  }, [courseContent]);


  if (!courseContent) {
    return <EmptyContentView course={course} onAddLessonClicked={onAddLessonClicked} onAddAssessmentClicked={onAddAssessmentClicked} onAddSectionClicked={() => onAddSectionClicked(1)} onContentTypeSelect={onContentTypeSelect} />;
  }

  return (
    <div className={styles.content}>
      {courseContent && entityType === "lesson" && (
        <EditLessonContent course={course} lesson={courseContent} />
      )}

      {courseContent && entityType === "assessment" && (
        <EditAssessmentContent course={course} assessment={courseContent} />
      )}

      {courseContent && entityType === "simulation" && (
        <EditSimulationContent course={courseContent as SimulationContent} />
      )}

      {courseContent && entityType === "podcast" && (
        <EditPodcastContent course={courseContent} />
      )}
    </div>
  );
}

interface EmptyContentViewProps {
  course: Course;
  // eslint-disable-next-line no-unused-vars
  onAddLessonClicked?: (parentUuid?: string | null) => void;

  // eslint-disable-next-line no-unused-vars
  onAddAssessmentClicked?: (parentUuid?: string | null) => void;

  // eslint-disable-next-line no-unused-vars
  onAddSectionClicked: () => void;
  onContentTypeSelect: (id?:string) => void;
}

const EmptyContentView: React.FC<EmptyContentViewProps> = ({
  course,
  onAddSectionClicked,
  onContentTypeSelect
}) => {

  const section = course?.contents?.find(c => c.entityType === 'section');

  return (
    <div className={styles.emptyContainer}>
      <div className={styles.heading}>nothing added yet!</div>
      <p className={styles.subText}>
        no learning content added to this course yet
      </p>

      <div className={styles.actions}>
        <Button variant="secondary" className={cx(styles.actionButton, styles.addContentButton)}
          icon="paperflite-add"
          onClick={() => onContentTypeSelect(section?.uuid)}
        >
          <span className={styles.text}>
            Add
            <span className={styles.primary}>Content</span>
          </span> 
        </Button>
        {_.size(course.contents) == 0 && 
          <Button variant="secondary" className={styles.actionButton}
            icon="paperflite-stack"
            onClick={onAddSectionClicked}
          >
            <span className={styles.text}>Add New <span className={styles.primary}>Section</span></span>
          </Button>
        }
      </div>
    </div>
  );
};

interface SelectContentTypeProps {
  course: Course;
  openSelectContentType: boolean;
  handleClose: () => void;
  onAddLessonClicked?: (parentUuid?: string | null) => void;
  onAddAssessmentClicked?: (parentUuid?: string | null) => void;
  onStudioClicked: () => void;
  handleFinishAddLessons: (content: any) => void;
  parentSectionId?: string | null;
}

export const SelectContentType: React.FC<SelectContentTypeProps> = ({
  openSelectContentType,
  handleClose,
  onAddAssessmentClicked,
  course,
  handleFinishAddLessons,
  parentSectionId,
}) => {
  const { showAssetsDialog, setShowAssetDialog } = useContext(CourseContext);
  const { selected, setSelected } = React.useContext(CourseContext);

  const [showLearningBrowser, setShowLearningBrowser] = useState(false);
  const [currentParentUuid, setCurrentParentUuid] = useState<string | null>(
    null,
  );

  const handleAddLessonClicked = () => {
    setCurrentParentUuid(currentParentUuid ?? null);
    setShowAssetDialog(true);
  };

  const selectScreenSpring = useSpring({
    transform:
      !showLearningBrowser && !showAssetsDialog
        ? "translateX(0%)"
        : "translateX(-100%)",
    opacity: !showLearningBrowser && !showAssetsDialog ? 1 : 1,
    config: { tension: 280, friction: 50 },
  });

  const assetScreenSpring = useSpring({
    transform:
      showLearningBrowser || showAssetsDialog
        ? "translateX(0%)"
        : "translateX(100%)",
    opacity: showLearningBrowser || showAssetsDialog ? 1 : 0,
    config: { tension: 280, friction: 30 },
  });

  const initialEntities = selected?.entities || [];

  const hasEntity = initialEntities?.length > 0;

  const handleAdd = () => {
    const payload = selected.entities.map((item) => {
      let contentToAdd = item;
  
      if (item.entityType === "podcast" || item.entityType === "simulation") {
        const course = item.course || {};
        const { callType, ...courseData } = course;
  
        contentToAdd = {
          ...item,          
          ...courseData,
          callType: callType
        };
  
        delete contentToAdd.course;
      }
  
      return {
        ...contentToAdd,
        id: contentToAdd.id,
        entityType: item.entityType,
        courseId: contentToAdd.courseId ?? contentToAdd.id,
        parentUuid: currentParentUuid,
      };
    });
  
    handleFinishAddLessons(payload);
    setSelected({ option: null, entities: [] });
    setShowLearningBrowser(false);
    setShowAssetDialog(false);
    handleClose();
  };

  const handleCancel = () => {
    setSelected({ option: null, entities: [] });
    setShowLearningBrowser(false);
    setShowAssetDialog(false);
    handleClose();
  };

  return (
    <Slider.Dialog
      width={hasEntity ? "720px" : "380px"}
      show={openSelectContentType}
      onClose={handleClose}
      className={styles.selectContentType}
    >
      <div className={styles.main}>
        <div>
          <animated.div
            style={selectScreenSpring}
            className={styles.contentType}
          >
            {!showLearningBrowser && !showAssetsDialog && (
              <Slider.Header title={"Add content"} className={styles.header}>
                <i className="paperflite-close" onClick={handleClose} />
              </Slider.Header>
            )}
          </animated.div>

          <div className={styles.body}>
            <div className={styles.left}>
              <animated.div
                style={selectScreenSpring}
                className={styles.contentType}
              >
                <SelectBody //main select with 3 options, lesson, assessment, studio
                  sectionUuid={parentSectionId}
                  onAddLessonClicked={handleAddLessonClicked}
                  onAddAssessmentClicked={onAddAssessmentClicked}
                  onStudioClicked={() => setShowLearningBrowser(true)}
                />
              </animated.div>

              {showLearningBrowser && (
                <animated.div
                  style={assetScreenSpring}
                  className={styles.assetBody}
                >
                  <AssetBody
                    onClose={() => setShowLearningBrowser(false)}
                    onFinish={() => {
                      setShowLearningBrowser(false);
                      handleClose();
                    }}
                  />
                </animated.div>
              )}
              <animated.div
                style={assetScreenSpring}
                className={styles.assetBody}
              >
                <AddAssetsDialog
                  className={styles.assets}
                  show={showAssetsDialog}
                  selectableEntityTypes={["asset"]}
                  initialEntities={initialEntities}
                  onSelectionChange={(entities) => {
                    setSelected({
                      ...selected,
                      entities,
                    });
                  }}
                  onFinish={handleAdd}
                  onBack={() => setShowAssetDialog(false)}
                  onClose={() => {
                    setShowAssetDialog(false);
                    handleClose();
                  }}
                />
              </animated.div>
            </div>
          </div>
          {hasEntity && (
            <Slider.Actions>
              <Button
                variant="primary"
                icon="paperflite-tick"
                text="Add"
                onClick={handleAdd}
                disabled={selected.entities.length === 0}
              />

              <Button
                variant="secondary"
                text="Cancel"
                onClick={handleCancel}
              />
            </Slider.Actions>
          )}
        </div>
        <div>
          {hasEntity && (
            <AssetSelection
              style={{}}
              state={{ option: null, entities: selected.entities }}
              onStateChange={(newState) => {
                setSelected({ ...selected, entities: newState.entities });
              }}
            />
          )}
        </div>
      </div>
    </Slider.Dialog>
  );
};
interface SelectBodyProps {
  sectionUuid?: string | null;
  onAddLessonClicked?: (parentUuid?: string | null) => void;
  onAddAssessmentClicked?: (parentUuid?: string | null) => void;
  onStudioClicked: () => void;
}

const SelectBody: React.FC<SelectBodyProps> = ({
  sectionUuid,
  onAddLessonClicked,
  onAddAssessmentClicked,
  onStudioClicked,
}) => {
  return (
    <div className={styles.wrapper}>
      <div
        className={styles.content}
        onClick={() => onAddLessonClicked?.(sectionUuid)}
      >
        <img src="/images/coaching/lessons.svg" alt="lesson" />
        <div className={styles.label}>
          <div className={styles.text}>Lessons</div>
          <div className={styles.subText}>
            Includes assets, links, files, URLs…
          </div>
          <div className={styles.actions}>
            <Button
              variant="tertiary"
              className={styles.action}
              icon="paperflite-add"
              text="Add"
            />
          </div>
        </div>
      </div>

      <div className={styles.divider} />

      <div
        className={styles.content}
        onClick={() => onAddAssessmentClicked?.(sectionUuid)}
      >
        <img src="/images/coaching/assessment-no-bg.svg" alt="assessment" />
        <div className={styles.label}>
          <div className={styles.text}>Assessment</div>
          <div className={styles.subText}>
            Test knowledge and learning with multi-format questions
          </div>
          <div className={styles.actions}>
            <Button
              variant="tertiary"
              className={styles.action}
              icon="paperflite-add"
              text="Add"
            />
          </div>
        </div>
      </div>

      <div className={styles.divider} />

      <div className={styles.content} onClick={onStudioClicked}>
        <img src="/images/coaching/studio.svg" alt="studio" />
        <div className={styles.label}>
          <div className={styles.text}>Podcasts & Simulations</div>
          <div className={styles.subText}>Audio learning experiences and AI roleplays</div>
          <div className={styles.actions}>
            <Button
              variant="tertiary"
              className={styles.action}
              icon="paperflite-add"
              text="Add"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

interface AssetBodyProps {
  onClose: () => void;
  onFinish: () => void;
}

const AssetBody: React.FC<AssetBodyProps> = ({ onClose, onFinish }) => {
  return (
    <AddLearningBrowser //pick from studio
      width="380px"
      show
      onClose={onClose}
    />
  );
};



